import { z } from 'zod';

/**
 * Runtime validator for the ERS schema JSON.
 *
 * Tolerant of real-world shape variations in v1.0.0:
 *   - `notes_for_clarification.general` nullable (e.g. Item 10 pending QA).
 *   - `asterisk_note` is boolean | string | null (some indicators embed the
 *     footnote text directly rather than using a separate flag).
 *   - `na_condition` / `observe_instruction` optional — defaulted to null.
 *   - `total_indicators` nullable for the stubbed ECERS-3 entry.
 */

export const zIndicatorResponse = z.enum(['Y', 'N', 'NA']);

const zIndicator = z
  .object({
    id: z.string(),
    level: z.union([z.literal(1), z.literal(3), z.literal(5), z.literal(7)]),
    sequence: z.number().int().positive(),
    code: z.string(),
    text: z.string(),
    example_text: z.string().nullable().optional(),
    allows_na: z.boolean(),
    na_condition: z.string().nullable().optional(),
    observe_instruction: z.string().nullable().optional(),
    asterisk_note: z.union([z.boolean(), z.string(), z.null()]).optional(),
  })
  .transform((ind) => ({
    ...ind,
    example_text: ind.example_text ?? null,
    na_condition: ind.na_condition ?? null,
    observe_instruction: ind.observe_instruction ?? null,
    asterisk_note: ind.asterisk_note ?? false,
  }));

const zIndicatorClarificationNote = z.object({
  applies_to_codes: z.array(z.string()),
  note: z.string(),
});

const zNotesForClarification = z.object({
  general: z.string().nullable(),
  indicator_notes: z.array(zIndicatorClarificationNote),
});

const zItem = z.object({
  id: z.string(),
  instrument_id: z.string(),
  subscale_id: z.string(),
  number: z.number().int().positive(),
  name: z.string(),
  page_reference: z.number().int().nullable().optional(),
  item_allows_na: z.boolean(),
  item_na_condition: z.string().nullable().optional(),
  notes_for_clarification: zNotesForClarification,
  indicators: z.array(zIndicator),
});

const zSubscale = z.object({
  id: z.string(),
  instrument_id: z.string(),
  number: z.number().int().positive(),
  name: z.string(),
  items: z.array(zItem),
});

const zInstrument = z.object({
  id: z.string(),
  full_name: z.string(),
  short_name: z.string(),
  edition: z.number(),
  year_published: z.number(),
  setting: z.string(),
  age_range_description: z.string(),
  age_range_months: z.object({
    min: z.number(),
    max: z.number(),
  }),
  observation_duration_hours: z.number(),
  total_subscales: z.number(),
  total_items: z.number(),
  total_indicators: z.number().nullable(),
  publisher: z.string(),
  authors: z.array(z.string()),
  indicator_response_options: z.array(zIndicatorResponse),
  // The manual's glossary (pp. 12-14). Optional so older schema files that
  // predate the transcription still validate.
  explanation_of_terms: z
    .object({
      title: z.string(),
      source_pages: z.array(z.number()).default([]),
      terms: z.array(
        z.object({ term: z.string(), definition: z.string() }),
      ),
    })
    .optional(),
  // "Administration of the Scale" / "Scoring System" (manual pp. 9-11).
  administration: z
    .array(
      z.object({
        heading: z.string(),
        source_pages: z.array(z.number()).default([]),
        blocks: z.array(
          z.object({
            kind: z.enum(['numbered', 'bullets', 'para', 'subheading']),
            number: z.number().nullable().default(null),
            text: z.string().default(''),
            items: z.array(z.string()).optional(),
          }),
        ),
      }),
    )
    .optional(),
  subscales: z.array(zSubscale),
});

export const zErsSchema = z.object({
  _metadata: z.object({
    description: z.string(),
    schema_version: z.string(),
    generated_date: z.string(),
    author: z.string(),
    instruments_included: z.array(z.string()),
    population_status: z.string(),
    notes: z.array(z.string()),
    last_updated: z.string(),
  }),
  scoring_algorithm: z.object({
    description: z.string(),
    rules: z.array(
      z.object({
        score: z.number(),
        label: z.string().nullable(),
        condition: z.string(),
      }),
    ),
    na_rules: z.array(z.string()),
    subscale_score: z.string(),
    total_scale_score: z.string(),
  }),
  rating_scale_levels: z.array(
    z.object({
      score: z.number(),
      label: z.string().nullable(),
      color_hex: z.string(),
    }),
  ),
  instruments: z.array(zInstrument),
});
